import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertCircle, Check, CheckCircle, Loader2, Radar } from 'lucide-react';
import { useVarreduraStatusCliente } from '../api';

interface Props {
  open: boolean;
  cpf: string | null;
  onClose: () => void;
  onFinished?: () => void;
}

// Failsafe: se o Python travar e o pid check falhar, fechar após 6 min.
// Recheck individual real demora ~150s, então 6 min é folga generosa.
const FAILSAFE_MS = 360_000;

type Phase = 'starting' | 'running' | 'finished' | 'error' | 'timeout';

export function VarreduraClienteProgressModal({ open, cpf, onClose, onFinished }: Props) {
  const { data } = useVarreduraStatusCliente(cpf, open);
  const [log, setLog] = useState<string[]>([]);
  const [phase, setPhase] = useState<Phase>('starting');
  const [openedAt, setOpenedAt] = useState<number>(0);
  const lastAtualRef = useRef(0);
  const finishedFiredRef = useRef(false);
  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setLog([]);
    setPhase('starting');
    setOpenedAt(Date.now());
    lastAtualRef.current = 0;
    finishedFiredRef.current = false;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (phase === 'finished' || phase === 'error') return;
    const t = setTimeout(() => setPhase('timeout'), FAILSAFE_MS);
    return () => clearTimeout(t);
  }, [open, phase]);

  useEffect(() => {
    if (!open || !data) return;
    if (data.status === 'concluido') {
      setPhase('finished');
      return;
    }
    if (data.status === 'erro') {
      setPhase('error');
      return;
    }
    if (data.em_andamento || data.atual > 0) {
      setPhase('running');
      return;
    }
    // Status 'ocioso' persistindo após 20s = Python provavelmente crashou
    // antes de escrever o arquivo de progresso.
    if (data.status === 'ocioso' && Date.now() - openedAt > 20_000 && phase !== 'running') {
      setPhase('error');
    }
  }, [open, data, openedAt, phase]);

  useEffect(() => {
    if (!data?.nome_atual) return;
    if (data.atual > lastAtualRef.current) {
      setLog((prev) => [...prev, data.nome_atual!]);
      lastAtualRef.current = data.atual;
    }
  }, [data]);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [log]);

  useEffect(() => {
    if (phase === 'finished' && !finishedFiredRef.current) {
      finishedFiredRef.current = true;
      onFinished?.();
    }
  }, [phase, onFinished]);

  const total = data?.total || 0;
  const atual = data?.atual || 0;
  const pct = total > 0 ? Math.min(100, (atual / total) * 100) : phase === 'finished' ? 100 : 0;
  const nomeAtual = data?.nome_atual && data.nome_atual !== '—' ? data.nome_atual : null;

  const isTerminal = phase === 'finished' || phase === 'error' || phase === 'timeout';

  const headerTitle =
    phase === 'finished'
      ? 'Diagnóstico concluído'
      : phase === 'error'
        ? 'Erro durante diagnóstico'
        : phase === 'timeout'
          ? 'Diagnóstico expirado'
          : phase === 'starting'
            ? 'Iniciando diagnóstico…'
            : 'Diagnóstico em andamento';

  const headerSub =
    phase === 'finished'
      ? 'Resultados atualizados.'
      : phase === 'error'
        ? nomeAtual || 'O script Python parou de responder. Tente de novo.'
        : phase === 'timeout'
          ? 'Sem resposta após 6 minutos. O processo pode estar pendurado.'
          : phase === 'starting'
            ? 'Aguardando primeira integração…'
            : nomeAtual || 'Processando…';

  const isDanger = phase === 'error' || phase === 'timeout';

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        if (!v && isTerminal) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[2100] bg-black/50 backdrop-blur-md" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[2100] -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-lg rounded-2xl border border-border bg-elevated shadow-[var(--shadow-over)] overflow-hidden">
          <div
            className="px-6 py-5 border-b border-border relative overflow-hidden"
            style={{
              background: isDanger
                ? 'linear-gradient(135deg, rgba(220,38,38,0.08) 0%, rgba(220,38,38,0.02) 100%)'
                : 'linear-gradient(135deg, rgba(217,119,87,0.08) 0%, rgba(217,119,87,0.02) 100%)',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center relative"
                style={{ background: isDanger ? 'rgba(220,38,38,0.12)' : 'rgba(217,119,87,0.12)' }}
              >
                {phase === 'finished' ? (
                  <CheckCircle size={22} strokeWidth={1.5} className="text-ok" />
                ) : isDanger ? (
                  <AlertCircle size={22} strokeWidth={1.5} className="text-danger" />
                ) : (
                  <>
                    <Radar size={22} strokeWidth={1.5} className="text-accent animate-pulse" />
                    <span
                      className="absolute inset-0 rounded-xl border-2 border-accent/20 animate-ping"
                      style={{ animationDuration: '2s' }}
                    />
                  </>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <Dialog.Title className="text-base font-bold text-text">{headerTitle}</Dialog.Title>
                <p className="text-xs text-text-2 mt-0.5 truncate">{headerSub}</p>
              </div>
              {!isTerminal && total > 0 && (
                <div className="shrink-0 text-right">
                  <div className="text-2xl font-bold text-accent tabular-nums leading-none">
                    {atual}
                    <span className="text-text-muted">/{total}</span>
                  </div>
                  <div className="text-[10px] text-text-muted uppercase tracking-wider mt-1">
                    {Math.round(pct)}%
                  </div>
                </div>
              )}
            </div>
            <div className="mt-4 h-1 bg-border rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-[width] duration-500 ease-linear"
                style={{
                  width: `${pct}%`,
                  background: isDanger
                    ? 'var(--danger)'
                    : phase === 'finished'
                      ? 'var(--ok)'
                      : 'var(--accent)',
                }}
              />
            </div>
          </div>

          <div className="px-6 py-4 max-h-[340px] overflow-y-auto">
            {log.length === 0 && phase === 'starting' && (
              <div className="flex items-center gap-2 text-xs text-text-muted">
                <Loader2 size={12} strokeWidth={2} className="animate-spin" />
                <span>Conectando às integrações públicas…</span>
              </div>
            )}
            {isDanger && log.length === 0 && (
              <p className="text-xs text-danger">{headerSub}</p>
            )}
            {log.length > 0 && (
              <ul className="flex flex-col gap-1.5">
                {log.map((label, i) => {
                  const isCurrent = i === log.length - 1 && phase === 'running';
                  return (
                    <li key={`${i}-${label}`} className="flex items-center gap-2.5 text-xs">
                      <span className="shrink-0 w-4 h-4 inline-flex items-center justify-center">
                        {isCurrent ? (
                          <Loader2 size={12} strokeWidth={2} className="text-accent animate-spin" />
                        ) : (
                          <Check size={14} strokeWidth={2} className="text-ok" />
                        )}
                      </span>
                      <span className={isCurrent ? 'text-text font-medium' : 'text-text-2'}>
                        {label}
                      </span>
                    </li>
                  );
                })}
                <div ref={logEndRef} />
              </ul>
            )}
          </div>

          <div className="px-6 py-3 border-t border-border bg-surface flex items-center justify-between gap-3">
            <span className="text-[11px] text-text-muted">
              {isTerminal ? '' : 'Pode minimizar — a varredura continua em background.'}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="text-xs font-medium px-3 py-1.5 rounded-md text-text-2 hover:bg-surface2 transition-colors"
            >
              {isTerminal ? 'Fechar' : 'Minimizar'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
